import {
    Dialog,
    DialogContent,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import React, { useState } from 'react';
import { LazyImage } from '../components/custom/LazyImage';
import { NavigationBarBreadCrumbs } from '@/components/molecules/BackNav';

import PageWrapper from '@/components/atoms/PageWrapper';
import BH_Photo from '../assets/images/srcdev_bh.png';
import PW_Photo from '../assets/images/srcdev_pw.jpg';
import RS_Photo from '../assets/images/srcdev_rs.jpg';

enum INITIALS {
    BH = "bh",
    RS = "rs",
    PW = "pw"
}

interface PersonnelInterface {
    name: string;
    imgSrc: string;
    position: string;
    initials: INITIALS;
    description: string;
}

const personnel: PersonnelInterface[] = [
    {
        name: 'Billy Huang',
        imgSrc: BH_Photo,
        position: 'Lead, Source Development Hub',
        initials: INITIALS.BH,
        description: `Billy Huang is a social entrepreneur and founder/CEO of Source Development Hub, an early-stage venture focused on designing tech tools to address issues of social and health inequity. Billy’s interests span areas related to social determinants of health, particularly at the intersection of housing, community health, and workforce development. 
        <br/>
        <br/>
        He has experience with machine-learning techniques, natural language processing, image analysis, data visualization, web development, and consulting. One of his current engagements is working with MIT Solve, a social impact marketplace, on developing tools and models to measure outcomes and impact. 
        <br/>
        <br/>
        Billy also leads and consults for several groups on an ongoing basis including (1) the New Haven Commission on Disabilities on areas of advocacy and equity, (2) New Haven’s Community Services Administration on areas of landlord engagement, and (3) the Osteogenesis Imperfecta Foundation, on areas of patient-centered heath research. He holds a Bachelor of Science from the Massachusetts Institute of Technology and an MBA from the Yale School of Management.
        `
    },
    {
        name: 'Rebecca Spens',
        imgSrc: RS_Photo,
        position: 'Co-lead, MIT Solve',
        initials: INITIALS.RS,
        description: `Rebecca Spens is Solve’s Results Measurement Officer and focuses on using research methods to understand Solve’s effectiveness and impact.
        <br/>
        <br/>
        Before joining Solve, Rebecca worked on evaluation and research in UK government, most recently at the Ministry of Justice. Rebecca holds a Master’s in Development Practice from Emory University and a BA in Modern History and French from the University of St. Andrews. As a graduate student, she worked as a consultant evaluator for international NGOs in the US, Kenya, and Ivory Coast on global health, youth employment, and education projects.
        <br/>
        <br/>
        In her free time, Rebecca likes to run, hike, read, travel, and discover new places to eat with friends.
        `
    },
    {
        name: 'Pooja Wagh',
        imgSrc: PW_Photo,
        position: 'Advisor, MIT Solve',
        initials: INITIALS.PW,
        description: `Pooja Wagh is Director, Operations & Impact at MIT Solve. Pooja came to Solve in 2017 with over a decade of experience in international development, program evaluation, and data analysis in the private and nonprofit sectors. As Director of Operations & Impact, Pooja designs and oversees many of Solve's core operations, including selecting Solver teams, implementing the Solver support program, engaging Solve's Member community, and measuring the results of Solver teams' work and the impact of the partnerships Solve brokers. Pooja holds a Masters in Public Policy from the Harvard Kennedy School and a Bachelors in electrical engineering from MIT.`
    },
]

const Team: React.FC = () => {
    const [showModal, setShowModal] = useState(false);
    const [selectedMember, setSelectedMember] = useState<null | string>(null);

    const openModal = (initials: string) => {
        setShowModal(true);
        setSelectedMember(initials);
    };
    const closeModal = () => {
        setShowModal(false);
    };

    const renderPerson = (initials: string | null, personnel: PersonnelInterface[]) => {
        if (!initials) return null;

        const personData = personnel.find(person => person.initials === initials);
        if (personData) {
            return (
                <TeamMemberInfo
                    name={personData.name}
                    imgSrc={personData.imgSrc}
                    position={personData.position}
                    description={personData.description}
                />
            )
        }

        return null;
    }

    return (
        <>
            <NavigationBarBreadCrumbs.Set
                {
                ...{
                    currentPageName: "About Us",
                    pages: [
                        {
                            title: "Home",
                            goTo: `/welcome`
                        },
                    ]
                }
                }
            />

            <Dialog open={showModal} onOpenChange={closeModal} defaultOpen={false}>
                <DialogContent className="max-w-6xl pt-12">
                    <ScrollArea className="px-3 max-h-[85vh]">
                        <div className="flex m-auto sm:items-center">
                            {renderPerson(selectedMember, personnel)}
                        </div>
                    </ScrollArea>
                </DialogContent>
            </Dialog>

            <PageWrapper className='flex flex-col items-center flex-1 w-full max-w-full custom_bg'>
                <div style={{ maxWidth: 1200 }} className="w-full p-0 m-auto">
                    <div className="flex items-center justify-center p-2 m-0 sm:py-5">
                        <div className="p-3 py-4 m-0 rounded sm:py-5 sm:px-5 bg-white/90">
                            <div className="p-0 m-0">
                                <h1 className="text-2xl font-bold sm:text-3xl">The MeasuringTogether Team</h1>
                            </div>

                            <div className="pt-4 pt-sm-5">
                                <h5 className="m-0 text-lg sm:text-xl">
                                    We are a team passionate about social impact and results measurement. Created through a collaboration with MIT Solve, a global social impact accelerator and marketplace, our team is tackling the challenge of improving data collection and outcomes measurement through the design and development of digital platforms.
                                </h5>
                            </div>

                            <div className="flex flex-col flex-wrap items-center justify-center p-0 pt-4 sm:flex-row sm:pt-5" style={{ margin: '0 -0.5rem 0 -0.5rem' }}>
                                {
                                    personnel.map(({ name, imgSrc, position, initials }, index) => (
                                        <TeamMember
                                            key={index}
                                            name={name}
                                            imgSrc={imgSrc}
                                            position={position}
                                            openModal={() => openModal(initials)}
                                        />
                                    ))
                                }
                            </div>
                        </div>
                    </div>
                </div>
            </PageWrapper>
        </>
    )
}

interface TeamMemberProps {
    name: string;
    imgSrc: string;
    position: string;
    openModal: () => void;
}

const TeamMember: React.FC<TeamMemberProps> = ({
    name,
    imgSrc,
    position,
    openModal
}) => {
    return (
        <div className="w-6/12 mb-4 cursor-pointer md:w-4/12" onClick={openModal}>
            <div className={`px-2`} >
                <LazyImage height={328} className='rounded team-photo' src={imgSrc} alt="Team Member Photo"></LazyImage>
            </div>
            <div className="mt-3 text-center">
                <h4 className={`font-bold`}>{name}</h4>
                <h6 className={`m-0 mt-2 p-0`}>{position}</h6>
            </div>
        </div>
    )
}

interface TeamMemberInfoProps {
    name: string;
    imgSrc: string;
    position: string;
    description: string;
}

const TeamMemberInfo: React.FC<TeamMemberInfoProps> = ({
    name,
    imgSrc,
    position,
    description
}) => {
    return (
        <div className="flex flex-wrap justify-center pb-0 sm:px-2 ">

            <div className="w-full cursor-pointer sm:w-5/12 md:w-4/12 lg:w-3/12">
                <div className={`skeleton`} >
                    <img className='rounded w-100' style={{ objectFit: 'cover' }} src={imgSrc} alt="..." />
                </div>
                <div className="my-3 text-center">
                    <h4 className={`fw-bold text-2xl`}>{name}</h4>
                    <h6 className={`m-0 mt-2 p-0 text-base`}>{position}</h6>
                </div>
            </div>

            <div className="w-full pb-3 sm:ps-5 sm:w-7/12 md:w-8/12 lg:w-9/12">
                <p className="about__modal-desc" dangerouslySetInnerHTML={{ __html: description }}></p>
            </div>
        </div>
    )
}

export default Team;
